import { useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { TicketCommentView } from '../../../services/TicketService';
import { addComment, removeComment } from '../state';
import { useMsal, useAccount } from '@azure/msal-react';

// Hook types for notes management
export interface UseNotesManagement {
  notes: TicketCommentView[];
  sortedNotes: TicketCommentView[];
  totalNotes: number;
  hasNotes: boolean;
  lastNote?: TicketCommentView;
  addNote: (noteText: string) => void;
  deleteNote: (noteId: string) => void;
  canDeleteNote: (note: TicketCommentView) => boolean;
}

export const useNotesManagement = (): UseNotesManagement => {
  const dispatch = useAppDispatch();
  
  // Get current authenticated user
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const currentUserName = account?.name || 'Unknown User';
  
  const { currentTicket } = useAppSelector(state => state.ticket);
  
  const notes = currentTicket?.communication?.comments || [];
  
  // Sort notes newest first (matching Parsec pattern)
  const sortedNotes = useMemo(() => 
    [...notes].sort((a, b) => 
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    ), [notes]
  );
  
  // Simple actions that just update Redux state (no async operations)
  const addNoteAction = useCallback((noteText: string) => {
    const newNote = {
      comment: noteText,
      author: currentUserName,
      createdDate: new Date().toISOString(),
      age: 'Just now'
    };
    
    dispatch(addComment(newNote)); // Simple Redux action
  }, [dispatch, currentUserName]);
  
  const deleteNoteAction = useCallback((noteId: string) => {
    dispatch(removeComment(noteId)); // Simple Redux action
  }, [dispatch]);
  
  const canDeleteNote = useCallback((note: TicketCommentView) => {
    if (!currentUserName) return false;
    return note.author === currentUserName;
  }, [currentUserName]);
  
  return {
    notes,
    sortedNotes,
    totalNotes: notes.length,
    hasNotes: notes.length > 0,
    lastNote: sortedNotes[0],
    addNote: addNoteAction,
    deleteNote: deleteNoteAction,
    canDeleteNote
  };
};
